import {
  Add,
  ArrowDownward,
  ArrowLeft,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { Backdrop, Button } from "@mui/material";
import { PaymentMethod } from "@stripe/stripe-js";
import cx from "classnames";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import DotElastic from "components/DotElastic";
import AddPaymentMethod, {
  AddPaymentMethodRef,
} from "components/Payment/UpgradeFlowComponents/AddPaymentMethod";
import Card from "components/Payment/UpgradeFlowComponents/Card";
import PlanSelected from "components/Payment/UpgradeFlowComponents/PlanSelected";
import {
  SLAPSHOT_MY_PLAN_UPGRADE_URL,
  SLAPSHOT_MY_PLAN_URL,
  SLAPSHOT_SUBSCRIPTION_UPGRADE_URL,
} from "constants/urls";
import { UserContext } from "layouts/SlapshotMainLayout";
import { InvoiceItem, Plan } from "models/Payment";
import { Subscription } from "models/User";
import { REVIEW_PURCHASE_INDEX } from "pages/UpgradePlanFlow/UpgradePlanFlow";
import {
  getPaymentMethods,
  setDefaultPaymentMethod,
} from "services/SubscriptionService";

import styles from "./SelectPaymentMethod.module.scss";

interface SelectPaymentMethodProps {
  onBack?: () => void;
  plan: Plan;
  selectedPaymentMethod: PaymentMethod | null;
  setSelectedPaymentMethod: React.Dispatch<
    React.SetStateAction<PaymentMethod | null>
  >;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  invoiceItems: InvoiceItem[] | null;
  subscription: Subscription;
}

const SelectPaymentMethod: React.FC<SelectPaymentMethodProps> = ({
  onBack,
  plan,
  setSelectedPaymentMethod,
  selectedPaymentMethod,
  setStep,
  invoiceItems,
  subscription,
}) => {
  const location = useLocation();

  const addPaymentMethodRef = useRef<AddPaymentMethodRef>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[] | null>(
    null,
  );
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showOtherCards, setShowOtherCards] = useState(false);
  const [defaultMethod, setDefaultMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const onCardRemove = useCallback(
    (paymentMethodId) => {
      setPaymentMethods([
        ...(paymentMethods?.filter((pm) => pm.id !== paymentMethodId) || []),
      ]);
      if (defaultMethod?.id === paymentMethodId) setDefaultMethod(null);
      if (selectedPaymentMethod?.id === paymentMethodId)
        setSelectedPaymentMethod(null);
    },
    [
      defaultMethod?.id,
      paymentMethods,
      selectedPaymentMethod?.id,
      setSelectedPaymentMethod,
    ],
  );

  const fetchPaymentMethods = useCallback(() => {
    getPaymentMethods(subscription.id)
      .then((data) => {
        setPaymentMethods(data.payment_methods);
        if (data.default_payment_method) {
          setDefaultMethod(data.default_payment_method);
          setSelectedPaymentMethod(data.default_payment_method);
        }
        if (!data.payment_methods.length) setShowAddPaymentModal(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setSelectedPaymentMethod, subscription]);

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        {onBack && (
          <div className={styles.back} onClick={onBack}>
            <KeyboardArrowLeft className={styles.arrowLeft} />
            <span>Back</span>
          </div>
        )}

        {paymentMethods === null && (
          <div className={styles.loading}>
            <DotElastic />
          </div>
        )}

        <div className={styles.leftSection}>
          {showAddPaymentModal && (
            <div className={styles.addPaymentMethod}>
              <div className={styles.heading}>Enter card details</div>

              <AddPaymentMethod
                ref={addPaymentMethodRef}
                onSuccess={async (setupIntent) => {
                  setIsLoading(true);
                  setDefaultPaymentMethod(
                    subscription?.id,
                    setupIntent.payment_method,
                  )
                    .then((data) => {
                      setSelectedPaymentMethod(data);
                      setStep(REVIEW_PURCHASE_INDEX);
                    })
                    .finally(() => {
                      setShowAddPaymentModal(false);
                      setIsLoading(false);
                    });
                }}
                returnUrl={`${location.pathname.startsWith(SLAPSHOT_MY_PLAN_URL) ? SLAPSHOT_MY_PLAN_UPGRADE_URL : SLAPSHOT_SUBSCRIPTION_UPGRADE_URL}${location.search}&plan-id=${plan.id}`}
                subscription={subscription}
              />

              {!!paymentMethods?.length && (
                <Button
                  className={cx(
                    styles.slapShotButtonOutline,
                    styles.cancelAddPayment,
                  )}
                  onClick={() => {
                    setShowAddPaymentModal(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}

          {!!paymentMethods?.length && !showAddPaymentModal && (
            <div className={styles.paymentMethods}>
              <div className={styles.heading}>
                Select a card for the payment
              </div>

              {selectedPaymentMethod && (
                <Card
                  key={selectedPaymentMethod.id}
                  paymentMethod={selectedPaymentMethod}
                  onClick={(pm) => {
                    setSelectedPaymentMethod(pm);
                  }}
                  isDefault={selectedPaymentMethod.id === defaultMethod?.id}
                  setDefaultMethod={setDefaultMethod}
                  isSelected
                  onRemove={() => {
                    onCardRemove(selectedPaymentMethod.id);
                  }}
                  hideOptions={
                    !!subscription &&
                    subscription.plan.price.unit_amount_decimal > 0 &&
                    selectedPaymentMethod.id === defaultMethod?.id
                  }
                  subscription={subscription}
                />
              )}

              {paymentMethods.filter(
                (paymentMethod) =>
                  paymentMethod.id !== selectedPaymentMethod?.id,
              ).length >= 1 && (
                <div
                  className={styles.showOtherCards}
                  onClick={() => setShowOtherCards((value) => !value)}
                >
                  Show other saved cards
                  {showOtherCards ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </div>
              )}

              {showOtherCards && (
                <div className={styles.otherCards}>
                  {paymentMethods
                    .filter(
                      (paymentMethod) =>
                        paymentMethod.id !== selectedPaymentMethod?.id,
                    )
                    .map((paymentMethod) => {
                      return (
                        <Card
                          key={paymentMethod.id}
                          setDefaultMethod={setDefaultMethod}
                          paymentMethod={paymentMethod}
                          onClick={(pm) => {
                            setSelectedPaymentMethod(pm);
                          }}
                          isDefault={paymentMethod.id === defaultMethod?.id}
                          onRemove={() => {
                            onCardRemove(paymentMethod.id);
                          }}
                          hideOptions={
                            !!subscription &&
                            subscription.plan.price.unit_amount_decimal > 0 &&
                            paymentMethod.id === defaultMethod?.id
                          }
                          subscription={subscription}
                        />
                      );
                    })}
                </div>
              )}

              <Button
                className={cx(styles.slapShotButton, styles.addCard)}
                onClick={() => {
                  setShowAddPaymentModal(true);
                }}
              >
                <Add fontSize="small" /> Add new card
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.rightSection}>
        <PlanSelected
          subscription={subscription}
          invoiceItems={invoiceItems}
          plan={plan}
          submitText="Agree & Continue"
          disabled={
            subscription?.is_processing_payment ||
            (!selectedPaymentMethod && !showAddPaymentModal)
          }
          onSubmit={() => {
            if (showAddPaymentModal) {
              addPaymentMethodRef.current?.confirm();
            } else {
              setStep(REVIEW_PURCHASE_INDEX);
            }
          }}
        />
      </div>

      <Backdrop open={isLoading} style={{ zIndex: 1 }}>
        <img src="/loading.gif" alt="" />
      </Backdrop>
    </div>
  );
};

export default SelectPaymentMethod;
